import { COLS, ROWS, TRACK_COUNT } from './constants'

const GRID_W = 1920
const GRID_H = 960
const FOCUS_SIZE = 960

const DIVIDER = 8
const EDGE = 8
const SELECT_LINE = 6

const INK = '#050607'
const SELECT_COLOUR = '#64d2ff'
const DIM = 'rgba(0,0,0,.62)'

/**
 * Paints the mosaic grid and the focused tile.
 *
 * Three things make this cheaper than the builds it replaces:
 *
 *  - **Chrome is pre-rendered.** Dimming, dividers, the perimeter and the
 *    selection outline are drawn once into an offscreen canvas and composited
 *    as a single image, instead of a dozen fill and stroke calls every frame.
 *    It is rebuilt only when the selection or a mute actually changes.
 *  - **The mosaic is one draw call.** The old mobile renderer issued eight
 *    separate `drawImage` calls, one per tile, sampling the video texture eight
 *    times to reassemble a layout the source already had.
 *  - **Repeat frames are skipped entirely.** The picture is a function of the
 *    musical frame index, so if that index has not advanced there is nothing
 *    new to paint. This matters most at slow tempos: at 25 % the video advances
 *    15 times a second while the display refreshes 60 times, so three out of
 *    four paints were pure waste.
 */
export class MosaicRenderer {
  private readonly gridCtx: CanvasRenderingContext2D
  private readonly focusCtx: CanvasRenderingContext2D
  private readonly chrome: HTMLCanvasElement
  private readonly chromeCtx: CanvasRenderingContext2D
  private readonly tile: HTMLCanvasElement
  private readonly tileCtx: CanvasRenderingContext2D

  private selected = 0
  private readonly muted: boolean[] = Array.from({ length: TRACK_COUNT }, () => false)

  private chromeDirty = true
  private lastFrameKey = -1
  private lastChromeVersion = -1
  private chromeVersion = 0

  constructor(gridCanvas: HTMLCanvasElement, focusCanvas: HTMLCanvasElement) {
    gridCanvas.width = GRID_W
    gridCanvas.height = GRID_H
    focusCanvas.width = FOCUS_SIZE
    focusCanvas.height = FOCUS_SIZE

    const grid = gridCanvas.getContext('2d', { alpha: false })
    const focus = focusCanvas.getContext('2d', { alpha: false })
    if (!grid || !focus) throw new Error('Could not create 2D drawing contexts.')
    this.gridCtx = grid
    this.focusCtx = focus

    this.chrome = document.createElement('canvas')
    this.chrome.width = GRID_W
    this.chrome.height = GRID_H
    const chromeCtx = this.chrome.getContext('2d')
    if (!chromeCtx) throw new Error('Could not create the overlay drawing context.')
    this.chromeCtx = chromeCtx

    this.tile = document.createElement('canvas')
    const tileCtx = this.tile.getContext('2d', { alpha: false })
    if (!tileCtx) throw new Error('Could not create the tile drawing context.')
    this.tileCtx = tileCtx

    for (const ctx of [this.gridCtx, this.focusCtx]) {
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
    }
  }

  setSelected(index: number): void {
    if (this.selected === index) return
    this.selected = index
    this.invalidateChrome()
  }

  setMuted(index: number, muted: boolean): void {
    if (this.muted[index] === muted) return
    this.muted[index] = muted
    this.invalidateChrome()
  }

  get selectedIndex(): number {
    return this.selected
  }

  /** Force a repaint on the next `draw`, e.g. after a resize or a new source. */
  invalidate(): void {
    this.lastFrameKey = -1
  }

  /**
   * Paint `source`. `frameId` identifies the frame being handed over, so a
   * repeat of the same frame costs nothing. Returns true if anything was drawn.
   */
  draw(source: CanvasImageSource | null, frameId: number): boolean {
    if (!source) return false

    if (frameId === this.lastFrameKey && this.chromeVersion === this.lastChromeVersion) {
      return false
    }
    this.lastFrameKey = frameId
    this.lastChromeVersion = this.chromeVersion

    const { width, height } = sourceSize(source)
    if (!width || !height) return false

    if (this.chromeDirty) this.renderChrome()

    // One draw call for the whole 4x2 atlas.
    this.gridCtx.drawImage(source, 0, 0, width, height, 0, 0, GRID_W, GRID_H)
    this.gridCtx.drawImage(this.chrome, 0, 0)

    // The focused tile is sampled from the source at full resolution rather
    // than upscaled from the grid canvas, which is the entire reason the
    // high-resolution mosaic is worth downloading.
    //
    // The crop must NOT use drawImage's source-rectangle form on the video
    // source: mobile WebKit (every iOS browser, whatever its name) has
    // long-standing bugs handling source rects for video and VideoFrame
    // sources, and silently draws the whole frame instead — the entire mosaic
    // appeared in the focus pane. Instead the frame is drawn translated onto a
    // tile-sized canvas, whose edges do the cropping, and that canvas is then
    // scaled into the focus view. Same pixels, no cropped-video drawImage.
    const tileW = width / COLS
    const tileH = height / ROWS
    const sx = Math.round((this.selected % COLS) * tileW)
    const sy = Math.round(Math.floor(this.selected / COLS) * tileH)
    const tw = Math.max(1, Math.round(tileW))
    const th = Math.max(1, Math.round(tileH))
    if (this.tile.width !== tw || this.tile.height !== th) {
      this.tile.width = tw
      this.tile.height = th
    }
    this.tileCtx.drawImage(source, -sx, -sy)
    this.focusCtx.drawImage(this.tile, 0, 0, tw, th, 0, 0, FOCUS_SIZE, FOCUS_SIZE)

    return true
  }

  private invalidateChrome(): void {
    this.chromeDirty = true
    this.chromeVersion += 1
  }

  private renderChrome(): void {
    const ctx = this.chromeCtx
    const tw = GRID_W / COLS
    const th = GRID_H / ROWS

    ctx.clearRect(0, 0, GRID_W, GRID_H)

    ctx.fillStyle = DIM
    for (let i = 0; i < TRACK_COUNT; i++) {
      if (!this.muted[i]) continue
      ctx.fillRect((i % COLS) * tw, Math.floor(i / COLS) * th, tw, th)
    }

    // Dividers and perimeter live in the same coordinate system as the mosaic,
    // so they can never sit a fraction of a pixel off the underlying image.
    ctx.fillStyle = INK
    ctx.fillRect(0, 0, GRID_W, EDGE)
    ctx.fillRect(0, GRID_H - EDGE, GRID_W, EDGE)
    ctx.fillRect(0, 0, EDGE, GRID_H)
    ctx.fillRect(GRID_W - EDGE, 0, EDGE, GRID_H)
    for (let c = 1; c < COLS; c++) ctx.fillRect(c * tw - DIVIDER / 2, 0, DIVIDER, GRID_H)
    for (let r = 1; r < ROWS; r++) ctx.fillRect(0, r * th - DIVIDER / 2, GRID_W, DIVIDER)

    const x = (this.selected % COLS) * tw
    const y = Math.floor(this.selected / COLS) * th
    ctx.strokeStyle = SELECT_COLOUR
    ctx.lineWidth = SELECT_LINE
    ctx.strokeRect(x + SELECT_LINE / 2, y + SELECT_LINE / 2, tw - SELECT_LINE, th - SELECT_LINE)

    this.chromeDirty = false
  }
}

function sourceSize(source: CanvasImageSource): { width: number; height: number } {
  if (source instanceof HTMLVideoElement) {
    return { width: source.videoWidth, height: source.videoHeight }
  }
  if (source instanceof HTMLImageElement) {
    return { width: source.naturalWidth, height: source.naturalHeight }
  }
  if (typeof VideoFrame !== 'undefined' && source instanceof VideoFrame) {
    return { width: source.displayWidth, height: source.displayHeight }
  }
  const sized = source as { width?: number; height?: number }
  return { width: sized.width ?? 0, height: sized.height ?? 0 }
}
